/**
 * DualPromptReview - Phase 2 of Character Builder
 *
 * Displays the split prompts (body + head) with:
 * - Hardcoded requirements shown as non-editable constraints
 * - AI-enhanced prompts that can be edited
 * - Back / Confirm navigation
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  SparkleIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CircleNotchIcon,
  LockIcon,
  UserIcon,
  UsersIcon,
  SmileyIcon,
  PaletteIcon,
  InfoIcon,
  ImageIcon,
} from '@phosphor-icons/react';
import { splitCharacterPrompt, SplitPrompts, CharacterConceptAnalysis } from '../../lib/geminiCharacterAnalysis';
import { getLayerConstraintsForDisplay } from '../../lib/layerPrompts';
import { CharacterForm, CharacterArchetype, ReferenceImage } from '../../types';
import { CHARACTER_ARCHETYPES } from '../../lib/characterArchetypes';
import { StyleOption } from '../../lib/characterStyles';

// Helper to convert strings to Title Case
const toTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Generation model type
type GenerationModelType = 'openai/gpt-image-2.5-sunburst' | 'prunaai/z-image-turbo';

interface DualPromptReviewProps {
  description: string;
  selectedTags: string[];
  analysis: CharacterConceptAnalysis;
  form: CharacterForm;
  archetype?: CharacterArchetype;
  selectedStyle?: StyleOption;
  referenceImages?: ReferenceImage[];  // User-uploaded reference images
  // Initial prompts to restore from saved state (skip regeneration)
  initialBodyPrompt?: string;
  initialHeadPrompt?: string;
  initialSharedAttributes?: SplitPrompts['sharedAttributes'];
  savedSourceDescription?: string;  // Description used when prompts were generated (for cache validation)
  onConfirm: (bodyPrompt: string, headPrompt: string, sharedAttributes: SplitPrompts['sharedAttributes'], generationModel: GenerationModelType, referenceImages?: ReferenceImage[]) => void;
  onBack: () => void;
  onReturnToCharacters?: () => void;  // Navigate back to My Characters list
  // Called immediately when prompts are generated (allows parent to save for back/forward persistence)
  onPromptsGenerated?: (splitPrompts: SplitPrompts, sourceDescription: string) => void;
  // Initial classified images to restore from saved state
  initialClassifiedImages?: SplitPrompts['classifiedImages'];
}

export default function DualPromptReview({
  description,
  selectedTags,
  analysis,
  form,
  archetype,
  selectedStyle,
  referenceImages,
  initialBodyPrompt,
  initialHeadPrompt,
  initialSharedAttributes,
  savedSourceDescription,
  onConfirm,
  onBack,
  onReturnToCharacters,
  onPromptsGenerated,
  initialClassifiedImages
}: DualPromptReviewProps) {
  // Split prompts state
  const [splitPrompts, setSplitPrompts] = useState<SplitPrompts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Prompts (read-only display from AI split)
  const [bodyPrompt, setBodyPrompt] = useState('');
  const [headPrompt, setHeadPrompt] = useState('');

  // Generation model selection (Default = Sunburst, Testing = z-image-turbo)
  const [generationModel, setGenerationModel] = useState<GenerationModelType>('openai/gpt-image-2.5-sunburst');

  // Get constraints for display
  const bodyConstraints = getLayerConstraintsForDisplay('body', form);
  const headConstraints = getLayerConstraintsForDisplay('head', form);

  // Get medium metadata
  const mediumMeta = archetype ? CHARACTER_ARCHETYPES[archetype] : null;

  // Get classification for a reference image from AI analysis
  const getImageClassification = (imageId: string): 'head' | 'body' | 'both' | undefined => {
    if (!splitPrompts?.classifiedImages) return undefined;
    const classified = splitPrompts.classifiedImages.find(c => c.imageId === imageId);
    return classified?.classification;
  };

  // Filter reference images for body layer (body or both classifications)
  const bodyReferenceImages = referenceImages?.filter(img => {
    const classification = getImageClassification(img.id);
    // Use AI classification if available, otherwise show all (will be classified on next render)
    if (!classification) return true;
    return classification === 'body' || classification === 'both';
  }) || [];

  // Filter reference images for head layer (head or both classifications)
  const headReferenceImages = referenceImages?.filter(img => {
    const classification = getImageClassification(img.id);
    // Use AI classification if available, otherwise show all (will be classified on next render)
    if (!classification) return true;
    return classification === 'head' || classification === 'both';
  }) || [];

  // Fetch split prompts on mount (skip if initial prompts match current description)
  useEffect(() => {
    // Use cached prompts if: we have them AND description hasn't changed since generation
    const canUseCachedPrompts = initialBodyPrompt && initialHeadPrompt &&
      savedSourceDescription && description === savedSourceDescription;

    if (canUseCachedPrompts) {
      console.log('📋 Using cached prompts (description unchanged)');
      setBodyPrompt(initialBodyPrompt);
      setHeadPrompt(initialHeadPrompt);
      if (initialSharedAttributes) {
        setSplitPrompts({
          bodyPrompt: initialBodyPrompt,
          headPrompt: initialHeadPrompt,
          sharedAttributes: initialSharedAttributes,
          classifiedImages: initialClassifiedImages  // Restore image classifications
        });
      }
      setIsLoading(false);
      return;
    }

    // Description changed or no cached prompts - regenerate
    const fetchSplitPrompts = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await splitCharacterPrompt(
          description,
          selectedTags,
          form,
          archetype,
          selectedStyle?.name,
          selectedStyle?.description,
          referenceImages  // Pass reference images for classification
        );
        setSplitPrompts(result);
        setBodyPrompt(result.bodyPrompt);
        setHeadPrompt(result.headPrompt);

        // Notify parent immediately so prompts can be saved for back/forward persistence
        if (onPromptsGenerated) {
          onPromptsGenerated(result, description);
        }
      } catch (err) {
        console.error('Failed to split prompts:', err);
        setError('Failed to process character description. Please try again.');
        // Fallback to basic split
        setBodyPrompt(description);
        setHeadPrompt(description);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSplitPrompts();
  }, [description, selectedTags, form, archetype, initialBodyPrompt, initialHeadPrompt, initialSharedAttributes, savedSourceDescription, referenceImages]);

  const handleConfirm = useCallback(() => {
    if (bodyPrompt.trim() && headPrompt.trim()) {
      onConfirm(
        bodyPrompt,
        headPrompt,
        splitPrompts?.sharedAttributes || {
          gender: analysis.inferredGender,
          ethnicity: 'Mixed',
          skinTone: 'medium'
        },
        'openai/gpt-image-2.5-sunburst',
        referenceImages
      );
    }
  }, [bodyPrompt, headPrompt, splitPrompts, analysis, onConfirm, generationModel, referenceImages]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-onyx text-white select-none items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <CircleNotchIcon className="w-8 h-8 text-slate-blue animate-spin" />
          <div className="text-center">
            <p className="text-lg text-white/90">Analyzing your character...</p>
            <p className="text-sm text-gray-500 mt-1">
              Splitting description into body and head prompts
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-onyx text-white select-none">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-b from-carbon to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <SparkleIcon className="w-5 h-5 text-slate-blue" />
              <h1 className="text-xl font-bold text-white">Review Split Prompts</h1>
            </div>
            {/* Medium/Style Badges */}
            <div className="flex items-center gap-2">
              {mediumMeta && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-carbon border border-iron/50 rounded-full">
                  <PaletteIcon className="w-4 h-4 text-slate-blue" />
                  <span className="text-sm font-medium text-gray-400">{mediumMeta.badge}</span>
                </div>
              )}
              {selectedStyle && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-blue/10 border border-slate-blue/30 rounded-full">
                  <SparkleIcon className="w-4 h-4 text-slate-blue" />
                  <span className="text-sm font-medium text-slate-blue-soft">{selectedStyle.name}</span>
                </div>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Your description has been split into body and head prompts. Review and edit if needed.
          </p>
          {error && (
            <p className="text-sm text-amber-400 mt-2">{error}</p>
          )}
        </div>
      </div>

      {/* Main Content - Two Columns */}
      <div className="flex-1 overflow-auto px-4 py-6">
        {/* Info Notice about filtered content */}
        <div className="max-w-5xl mx-auto mb-4">
          <div className="flex items-start gap-3 px-4 py-3 bg-slate-blue/10 border border-slate-blue/30 rounded-xl">
            <InfoIcon className="w-5 h-5 text-slate-blue flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-400">
              <span className="text-slate-blue-soft font-medium">Base Template Mode:</span>{' '}
              Clothing, accessories, backgrounds, and lighting have been filtered out. The body layer generates in minimal attire to serve as a base for layered outfits.
            </div>
          </div>
        </div>

        {/* Detected Attributes */}
        {splitPrompts?.sharedAttributes && (
          <div className="max-w-5xl mx-auto mb-4">
            <div className="bg-carbon rounded-xl px-5 py-4">
              <div className="flex items-center gap-2 mb-3">
                <SparkleIcon className="w-4 h-4 text-slate-blue" />
                <span className="text-xs uppercase tracking-widest text-gray-500">
                  Detected Attributes
                </span>
              </div>
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1.5 bg-graphite rounded-full text-sm text-gray-400">
                  Gender: <span className="text-white">{toTitleCase(splitPrompts.sharedAttributes.gender)}</span>
                </span>
                <span className="px-3 py-1.5 bg-graphite rounded-full text-sm text-gray-400">
                  Ethnicity: <span className="text-white">{toTitleCase(splitPrompts.sharedAttributes.ethnicity)}</span>
                </span>
                <span className="px-3 py-1.5 bg-graphite rounded-full text-sm text-gray-400">
                  Skin Tone: <span className="text-white">{toTitleCase(splitPrompts.sharedAttributes.skinTone)}</span>
                </span>
                {splitPrompts.sharedAttributes.age && (
                  <span className="px-3 py-1.5 bg-graphite rounded-full text-sm text-gray-400">
                    Age: <span className="text-white">{toTitleCase(splitPrompts.sharedAttributes.age)}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Body Column */}
          <div className="flex flex-col bg-carbon rounded-2xl overflow-hidden">
            {/* Column Header */}
            <div className="px-5 py-4 bg-graphite border-b border-iron/50">
              <div className="flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-slate-blue" />
                <span className="font-semibold text-white">Body Layer</span>
                <span className="text-xs text-gray-500 ml-auto">9:16 Portrait</span>
              </div>
            </div>

            {/* Hardcoded Requirements */}
            <div className="px-5 py-4 border-b border-iron/30">
              <div className="flex items-center gap-2 mb-3">
                <LockIcon className="w-4 h-4 text-gray-500" />
                <span className="text-xs uppercase tracking-widest text-gray-500">
                  Enforced Requirements
                </span>
              </div>
              <ul className="space-y-1.5">
                {bodyConstraints.map((constraint, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm text-gray-400"
                  >
                    <span className="text-slate-blue mt-0.5">•</span>
                    <span>{constraint}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Enhanced Prompt */}
            <div className="flex-1 p-5">
              <div className="mb-3">
                <span className="text-xs uppercase tracking-widest text-gray-500">
                  Enhanced Prompt
                </span>
              </div>
              <div className="bg-graphite/50 rounded-xl p-4 min-h-[160px]">
                {/* Reference images classified for body layer */}
                {bodyReferenceImages.length > 0 && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {bodyReferenceImages.map((img, idx) => {
                      const displayUrl = img.base64?.startsWith('data:')
                        ? img.base64
                        : img.base64
                          ? `data:image/png;base64,${img.base64}`
                          : img.url;
                      const classification = getImageClassification(img.id);
                      return (
                        <div key={img.id || idx} className="flex flex-col items-center gap-1">
                          <div className="w-14 h-14 rounded-lg overflow-hidden border border-iron/50">
                            <img src={displayUrl} alt="Reference" className="w-full h-full object-cover" />
                          </div>
                          <span className="text-[10px] text-gray-500">
                            {classification === 'body' ? 'Body' : classification === 'both' ? 'Full' : 'Ref'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-sm text-gray-400 leading-relaxed">
                  {bodyPrompt || 'No prompt generated'}
                </p>
              </div>
            </div>
          </div>

          {/* Head Column */}
          <div className="flex flex-col bg-carbon rounded-2xl overflow-hidden">
            {/* Column Header */}
            <div className="px-5 py-4 bg-graphite border-b border-iron/50">
              <div className="flex items-center gap-2">
                <SmileyIcon className="w-5 h-5 text-slate-blue" />
                <span className="font-semibold text-white">Head Layer</span>
                <span className="text-xs text-gray-500 ml-auto">1:1 Portrait</span>
              </div>
            </div>

            {/* Hardcoded Requirements */}
            <div className="px-5 py-4 border-b border-iron/30">
              <div className="flex items-center gap-2 mb-3">
                <LockIcon className="w-4 h-4 text-gray-500" />
                <span className="text-xs uppercase tracking-widest text-gray-500">
                  Enforced Requirements
                </span>
              </div>
              <ul className="space-y-1.5">
                {headConstraints.map((constraint, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm text-gray-400"
                  >
                    <span className="text-slate-blue mt-0.5">•</span>
                    <span>{constraint}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Enhanced Prompt */}
            <div className="flex-1 p-5">
              <div className="mb-3">
                <span className="text-xs uppercase tracking-widest text-gray-500">
                  Enhanced Prompt
                </span>
              </div>
              <div className="bg-graphite/50 rounded-xl p-4 min-h-[160px]">
                {/* Reference images classified for head layer */}
                {headReferenceImages.length > 0 && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {headReferenceImages.map((img, idx) => {
                      const displayUrl = img.base64?.startsWith('data:')
                        ? img.base64
                        : img.base64
                          ? `data:image/png;base64,${img.base64}`
                          : img.url;
                      const classification = getImageClassification(img.id);
                      return (
                        <div key={img.id || idx} className="flex flex-col items-center gap-1">
                          <div className="w-14 h-14 rounded-lg overflow-hidden border border-iron/50">
                            <img src={displayUrl} alt="Reference" className="w-full h-full object-cover" />
                          </div>
                          <span className="text-[10px] text-gray-500">
                            {classification === 'head' ? 'Head' : classification === 'both' ? 'Full' : 'Ref'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-sm text-gray-400 leading-relaxed">
                  {headPrompt || 'No prompt generated'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="px-4 py-6 bg-gradient-to-t from-carbon to-transparent">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          {/* Left Navigation Buttons */}
          <div className="flex items-center gap-3">
            {onReturnToCharacters && (
              <button
                onClick={onReturnToCharacters}
                className="flex items-center gap-2 px-4 py-3 rounded-xl text-gray-500 hover:text-white hover:bg-graphite transition-all text-sm"
              >
                <UsersIcon className="w-4 h-4" />
                <span>Return to Characters</span>
              </button>
            )}
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-3 rounded-xl text-gray-500 hover:text-white hover:bg-graphite transition-all text-sm"
            >
              <CaretLeftIcon className="w-4 h-4" />
              <span>Back to Builder</span>
            </button>
          </div>

          <div className="flex-1" />

          <span className="text-sm text-gray-400">ChatGPT · High quality</span>
          {/* Confirm Button */}
          <button
            onClick={handleConfirm}
            disabled={!bodyPrompt.trim() || !headPrompt.trim()}
            className={`flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-medium transition-all ${
              bodyPrompt.trim() && headPrompt.trim()
                ? 'bg-gradient-to-r from-slate-blue to-[#9795FA] text-white shadow-lg shadow-slate-blue/30 hover:shadow-slate-blue/50 active:scale-95'
                : 'bg-carbon text-gray-600 cursor-not-allowed'
            }`}
          >
            <span>Create & Refine Head · 2 credits</span>
            <CaretRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
